"use client";

import type { AsyncStorage } from "@tanstack/query-persist-client-core";

import {
  defaultShouldDehydrateQuery,
  QueryClient,
} from "@tanstack/react-query";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { persistQueryClient } from "@tanstack/react-query-persist-client";

/**
 * 持久缓存 QueryClient 工厂：`staleTime: 60s`（同 trex-site），
 * `gcTime: Infinity`（缓存不自动清，配合 localforage 持久化，刷新页面 /
 * 重新打开也有上一次数据可以立刻显示）。是否落盘由每个 query 的
 * `meta: { persist: true }` 白名单决定，未标记的 query 只走内存缓存。
 */
const queryClients = new Map<string, QueryClient>();

type TLocalForageModule = typeof import("localforage");
type TLocalForageInstance = ReturnType<TLocalForageModule["createInstance"]>;

function createStorage(appId: string): AsyncStorage<string> {
  let dbPromise: Promise<TLocalForageInstance> | undefined;

  function getDb(): Promise<TLocalForageInstance> {
    if (!dbPromise) {
      const env = process.env.NEXT_PUBLIC_CLIENT_ENV ?? "dev";
      dbPromise = import("localforage").then((localforage) =>
        localforage.createInstance({
          name: `trex-2b-${appId}-${env}`,
          storeName: "react_query_cache",
        }),
      );
    }
    return dbPromise;
  }

  return {
    getItem: async (key: string): Promise<string | null> => {
      try {
        return await (await getDb()).getItem<string>(key);
      } catch {
        // 隐私模式 / 存储禁用等：静默降级为纯内存缓存
        return null;
      }
    },
    setItem: async (key: string, value: string): Promise<void> => {
      try {
        await (await getDb()).setItem(key, value);
      } catch {
        // 配额满等：跳过写入，内存中的 QueryClient 仍可用
      }
    },
    removeItem: async (key: string): Promise<void> => {
      try {
        await (await getDb()).removeItem(key);
      } catch {
        // 忽略
      }
    },
  };
}

/** 每个 app 一份单例；`appId` 用于隔离各 app 的 IndexedDB（如 "aegis"）。 */
export function getQueryClient(appId: string): QueryClient {
  const cached = queryClients.get(appId);
  if (cached) {
    return cached;
  }

  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        gcTime: Infinity,
      },
    },
  });

  if (typeof window !== "undefined") {
    persistQueryClient({
      queryClient: client,
      persister: createAsyncStoragePersister({
        storage: createStorage(appId),
        throttleTime: 1000,
      }),
      dehydrateOptions: {
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) && query.meta?.persist === true,
        shouldDehydrateMutation: () => false,
      },
    });
  }

  queryClients.set(appId, client);
  return client;
}
